/*
 * Copyright (c) 2024. Spin in het Web B.V. Apeldoorn NL.
 * Deze software is géén open source. Het auteursrecht van deze software
 * ligt bij Spin in het Web B.V. en/of Jellybean B.V. en/of de auteur.
 * Gebruik van deze software is onderhevig aan een licentieovereenkomst.
 * www.spininhetweb.nl
 */

/*
* sihwlogger.ts
* 23-10-2024 - Jelmer Jellema - Spin in het Web B.V.
*/


import {hash} from "../types";
import {LocalConfig} from "../localconfig";

export class Sihwlog {
  //wat defaults want die zijn verplicht totdat alles geladen is
  loglevel: string = 'error';
  loglevelNr: number = 5;
  effectiveLoglevel: string = 'error';
  effectiveLoglevelNr: number = 5;
  private levels: hash<number> = {
    'debug': 1,
    'log': 2,
    'info': 3,
    'warn': 4,
    'error': 5
  };
  private conf = new LocalConfig();
  private readyPromise: Promise<void>;

  /**
   * Maak een Sihwlogger en zet alle functies goed
   * @param loglevel
   * @param prefix
   */
  constructor(loglevel?: string, prefix?: string) {
    //we wachten tot de conf er is. Tot die tijd gebeurt er niets
    //en we doen dit in onze eigen readypromise

    if (this.conf.loaded) {
      //is al async geladen
      this.init(loglevel, prefix); //kan dus sync
      this.readyPromise = Promise.resolve();
    } else {
      //hmm, de conf is er nog niet, gaan we op wachten
      this.readyPromise = new Promise(resolve => {
        this.conf.whenLoaded.then(() => {
          //we zijn klaar om te gaan
          this.init(loglevel, prefix); //ergens in de promise dus
          resolve();
        });
      });
    }
  }

  get ready(): Promise<void> {
    return this.readyPromise;
  }


  //de mogelijke calls
  //standaard in afwachting van onze ready, zodat logs wel komen,
  // worden overschreven in het async deel van de constructor:

  debug(...args: any[]) {
    this.ready.then(() => this.debug(...args, '(log preloaded)'));
  }

  log(...args: any[]): void {
    this.ready.then(() => this.log(...args, '(log preloaded)'));
  }

  info(...args: any[]): void {
    this.ready.then(() => this.info(...args, '(log preloaded)'));
  }

  warn(...args: any[]): void {
    this.ready.then(() => this.warn(...args, '(log preloaded)'));
  }

  error(...args: any[]): void {
    this.ready.then(() => this.error(...args, '(log preloaded)'));
  }

  /**
   * Doe het initwerk uit de constructor (sync of async)
   * @param loglevel
   * @param prefix
   * @private
   */
  private init(loglevel?: string, prefix?: string) {
    loglevel ??= <string>this.conf.$('SIHW.sihwlog.defaultLevel', 'warn');
    if (!(loglevel in this.levels)) {
      throw (`sihwlog: onbekend loglevel ${loglevel}`);
    }
    this.loglevel = loglevel;
    this.loglevelNr = this.levels[loglevel];
    const minLevelnr = this.levels[this.conf.$('SIHW.sihwlog.minLevel', 'warn')] || 4;
    this.effectiveLoglevelNr = Math.max(this.loglevelNr, minLevelnr);
    this.effectiveLoglevel = <string>Object.keys(this.levels).find(level => this.levels[level] === this.effectiveLoglevelNr);
    let enabled = this.conf.$('SIHW.sihwlog.logging', true);

    for (let level of Object.keys(this.levels)) {
      if (enabled && this.levels[level] >= minLevelnr && this.levels[level] >= this.loglevelNr) {
        let argarray: string[] = [];
        let consolelevel = level;
        if (level === 'debug') {
          argarray.push('*');
          consolelevel = 'log';
        }
        if (prefix) {
          argarray.push(`[${prefix}]`);
        }
        //even via any de functie veranderen
        (<any>this)[level] = (<hash<any>>console)[consolelevel].bind(console, ...argarray);
      }
      else {
        (<any>this)[level] = () => {}; //noop
      }
    }
  }
}
